import { z } from 'zod';

export const AdminNotificationChannelSchema = z.enum(['telegram', 'email', 'web_push']);
export type AdminNotificationChannel = z.infer<typeof AdminNotificationChannelSchema>;

export const AdminBroadcastStatusSchema = z.enum(['draft', 'sending', 'sent', 'failed']);
export type AdminBroadcastStatus = z.infer<typeof AdminBroadcastStatusSchema>;

export const AdminBroadcastPayloadSchema = z
  .object({
    subject: z.string().trim().min(1).max(120),
    title: z.string().trim().min(1).max(120),
    body: z.string().trim().min(1).max(2000),
    ctaLabel: z.string().trim().min(1).max(80).default('Открыть'),
    targetUrl: z
      .string()
      .trim()
      .min(1)
      .max(2048)
      .refine(
        (value) => value.startsWith('/') && !value.startsWith('//'),
        'Target URL must be a relative app path',
      ),
  })
  .strict();
export type AdminBroadcastPayloadDto = z.infer<typeof AdminBroadcastPayloadSchema>;

export const AdminBroadcastEstimateChannelSchema = z.object({
  channel: AdminNotificationChannelSchema,
  reachable: z.number().int().nonnegative(),
  skippedNoReachability: z.number().int().nonnegative(),
});
export type AdminBroadcastEstimateChannel = z.infer<typeof AdminBroadcastEstimateChannelSchema>;

export const AdminBroadcastAudienceEstimateSchema = z.object({
  totalUsers: z.number().int().nonnegative(),
  consentedUsers: z.number().int().nonnegative(),
  skippedNoBroadcastConsent: z.number().int().nonnegative(),
  qaAllowlistedUsers: z.number().int().nonnegative(),
  skippedQaAllowlist: z.number().int().nonnegative(),
  eligibleUsers: z.number().int().nonnegative(),
  sendsEnabled: z.boolean(),
  channels: z.array(AdminBroadcastEstimateChannelSchema),
});
export type AdminBroadcastAudienceEstimate = z.infer<typeof AdminBroadcastAudienceEstimateSchema>;

export const AdminBroadcastPreviewSchema = z.object({
  telegram: z.string(),
  email: z.object({
    subject: z.string(),
    preheader: z.string(),
    title: z.string(),
    intro: z.string(),
    ctaLabel: z.string(),
    targetUrl: z.string(),
  }),
  webPush: z.object({
    title: z.string(),
    body: z.string(),
    targetUrl: z.string(),
  }),
});
export type AdminBroadcastPreview = z.infer<typeof AdminBroadcastPreviewSchema>;

export const AdminBroadcastResultChannelSchema = z.object({
  channel: AdminNotificationChannelSchema,
  sent: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  skipReasons: z.record(z.number().int().nonnegative()),
});
export type AdminBroadcastResultChannel = z.infer<typeof AdminBroadcastResultChannelSchema>;

export const AdminBroadcastResultSummarySchema = z.object({
  totalUsers: z.number().int().nonnegative(),
  channels: z.array(AdminBroadcastResultChannelSchema),
});
export type AdminBroadcastResultSummary = z.infer<typeof AdminBroadcastResultSummarySchema>;

export const AdminBroadcastResponseSchema = z.object({
  id: z.string(),
  subject: z.string(),
  title: z.string(),
  body: z.string(),
  ctaLabel: z.string(),
  targetUrl: z.string(),
  status: AdminBroadcastStatusSchema,
  audienceEstimate: AdminBroadcastAudienceEstimateSchema.nullable(),
  resultSummary: AdminBroadcastResultSummarySchema.nullable(),
  preview: AdminBroadcastPreviewSchema,
  sentAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type AdminBroadcastResponse = z.infer<typeof AdminBroadcastResponseSchema>;

export const AdminBroadcastListResponseSchema = z.object({
  items: z.array(AdminBroadcastResponseSchema),
});
export type AdminBroadcastListResponse = z.infer<typeof AdminBroadcastListResponseSchema>;
